import type { Usp } from '@motorcity/shared';
import { LocalizedInput } from './Fields';

/**
 * USPs are ordered, so they get explicit move controls rather than drag and
 * drop — keyboard-reachable, and it works the same on a touch screen.
 */
export function UspEditor({
  value,
  onChange,
}: {
  value: Usp[];
  onChange: (next: Usp[]) => void;
}) {
  const update = (index: number, patch: Partial<Usp>) =>
    onChange(value.map((u, i) => (i === index ? { ...u, ...patch } : u)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    onChange(next);
  };

  return (
    <div className="field">
      <div className="field-header">
        <span className="field-label">Unique selling points</span>
        <button
          type="button"
          className="btn subtle"
          onClick={() =>
            onChange([
              ...value,
              { id: `usp-${crypto.randomUUID().slice(0, 8)}`, text: { en: '', ar: '' } },
            ])
          }
        >
          Add USP
        </button>
      </div>

      {value.length === 0 ? (
        <p className="inline-empty">No USPs yet — these become the highlight bullets in the app.</p>
      ) : null}

      <ol className="usp-list">
        {value.map((usp, index) => (
          <li key={usp.id} className="usp-item">
            <div className="usp-body">
              <LocalizedInput
                label={`USP ${index + 1}`}
                value={usp.text}
                onChange={(text) => update(index, { text })}
                required
              />
            </div>
            <div className="usp-controls">
              <button
                type="button"
                className="btn icon"
                aria-label="Move up"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn icon"
                aria-label="Move down"
                disabled={index === value.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="btn icon danger"
                aria-label="Remove"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
